import { createContext, useContext, useEffect, useMemo, useCallback, useState } from "react";
import * as site from "@/lib/site";

// Public website languages. English is the canonical/source-of-truth language.
export const LANGS = [
    { code: "en", label: "English", dir: "ltr", htmlLang: "en", ogLocale: "en_US" },
    { code: "ml", label: "മലയാളം", dir: "ltr", htmlLang: "ml", ogLocale: "ml_IN" },
    { code: "hi", label: "हिन्दी", dir: "ltr", htmlLang: "hi", ogLocale: "hi_IN" },
    { code: "ar", label: "العربية", dir: "rtl", htmlLang: "ar", ogLocale: "ar_SA" },
    { code: "es", label: "Español", dir: "ltr", htmlLang: "es", ogLocale: "es_ES" },
    { code: "ru", label: "Русский", dir: "ltr", htmlLang: "ru", ogLocale: "ru_RU" },
    { code: "de", label: "Deutsch", dir: "ltr", htmlLang: "de", ogLocale: "de_DE" },
    { code: "fr", label: "Français", dir: "ltr", htmlLang: "fr", ogLocale: "fr_FR" },
];

export const STORAGE_KEY = "universheal-lang";

// Deep-merge translated overrides onto the English base. Arrays merge
// item-by-item so translated text lands on top of the English items while
// technical fields (path, slug, status, icon...) survive from the base.
// Missing leaves always fall back to English.
const deepMerge = (base, override) => {
    if (override === undefined || override === null) return base;
    if (Array.isArray(base)) {
        if (!Array.isArray(override)) return base;
        return base.map((item, i) => {
            if (item !== null && typeof item === "object") {
                return deepMerge(item, override[i]);
            }
            return typeof override[i] === "string" && override[i].trim() !== ""
                ? override[i]
                : item;
        });
    }
    if (typeof base === "object" && base !== null && typeof override === "object") {
        const out = { ...base };
        for (const key of Object.keys(base)) {
            out[key] = deepMerge(base[key], override[key]);
        }
        return out;
    }
    return typeof override === "string" && override.trim() !== "" ? override : base;
};

const initialLang = () => {
    try {
        const fromUrl = new URLSearchParams(window.location.search).get("lang");
        const stored = window.localStorage.getItem(STORAGE_KEY);
        const pick = fromUrl || stored;
        return LANGS.some((l) => l.code === pick) ? pick : "en";
    } catch {
        return "en";
    }
};

const LanguageContext = createContext({
    lang: "en",
    setLang: () => {},
    t: (key, fallback = "") => fallback,
    site: site,
    langs: LANGS,
    langMeta: LANGS[0],
});

export const useLang = () => useContext(LanguageContext);

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(initialLang);
    const [overrides, setOverrides] = useState({});

    // Lazily load the selected language pack (English is bundled; the rest are
    // split into their own chunks so no other language costs anything up front).
    useEffect(() => {
        if (lang === "en") {
            setOverrides({});
            return;
        }
        let cancelled = false;
        import(`./langs/${lang}.json`)
            .then((mod) => {
                if (!cancelled) setOverrides(mod.default || {});
            })
            .catch(() => {
                if (!cancelled) setOverrides({});
            });
        return () => {
            cancelled = true;
        };
    }, [lang]);

    // Persistence + <html lang/dir> for fonts, hyphenation and RTL.
    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, lang);
        } catch {
            // storage unavailable — selection still applies for the session
        }
        const meta = LANGS.find((l) => l.code === lang) || LANGS[0];
        document.documentElement.lang = meta.htmlLang;
        document.documentElement.dir = meta.dir;
    }, [lang]);

    // Multilingual SEO hints: hreflang alternates (content is negotiated on the
    // client via ?lang=), canonical without the lang parameter.
    useEffect(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete("lang");
        const clean = url.toString();
        const head = document.head;
        const add = (rel, testidAttrs) => {
            const link = document.createElement("link");
            link.setAttribute("rel", rel);
            Object.entries(testidAttrs).forEach(([k, v]) => link.setAttribute(k, v));
            link.setAttribute("data-i18n-alternate", "true");
            head.appendChild(link);
            return link;
        };
        const links = LANGS.map((l) =>
            add("alternate", { hreflang: l.htmlLang, href: `${clean}?lang=${l.code}` })
        );
        links.push(add("alternate", { hreflang: "x-default", href: clean }));
        const canonical = add("canonical", { href: clean });
        return () => {
            links.forEach((l) => l.remove());
            canonical.remove();
        };
    }, []);

    const setLang = useCallback((code) => {
        if (LANGS.some((l) => l.code === code)) setLangState(code);
    }, []);

    const siteTree = useMemo(() => deepMerge(site, overrides.site || {}), [overrides]);

    // t("nav.cases", "Cases") — translated value from the active language pack,
    // falling back to the English text wired at the call site. Never renders a key.
    const t = useCallback(
        (key, fallback = "") => {
            const value = (overrides.strings || {})[key];
            return typeof value === "string" && value.trim() !== "" ? value : fallback;
        },
        [overrides]
    );

    // Rich-text variant for JSX titles: returns the translated HTML fragment
    // (accented with <em>) or the original JSX node as fallback.
    const tHtml = useCallback(
        (key, jsxNode) => {
            const s = (overrides.strings || {})[key];
            if (typeof s === "string" && s.trim() !== "") {
                return (
                    <span
                        className="i18n-html"
                        dangerouslySetInnerHTML={{ __html: s }}
                    />
                );
            }
            return jsxNode;
        },
        [overrides]
    );

    const langMeta = LANGS.find((l) => l.code === lang) || LANGS[0];

    return (
        <LanguageContext.Provider value={{ lang, setLang, t, tHtml, site: siteTree, langs: LANGS, langMeta }}>
            {children}
        </LanguageContext.Provider>
    );
}
